import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Landmark, ExternalLink, Loader2, CheckCircle2, AlertCircle } from "lucide-react";

export default function PayoutsPanel() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/connect/status");
      setStatus(data);
    } catch (_e) {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // If user just returned from Stripe onboarding, refresh state
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("connect") === "return" || params.get("connect") === "refresh") {
      load();
      // strip the query param so refresh doesn't re-poll
      const url = new URL(window.location.href);
      url.searchParams.delete("connect");
      window.history.replaceState({}, "", url.toString());
    }
  }, [load]);

  const onboard = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/connect/onboard", { origin_url: window.location.origin });
      window.location.href = data.onboarding_url;
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || "Could not start onboarding";
      if (status === 503) {
        // Platform hasn't enabled Connect yet — show a clear inline notice, not just a toast.
        setStatus((s) => ({ ...(s || {}), connect_disabled: true, disabled_reason: detail }));
      } else {
        toast.error(detail);
      }
      setBusy(false);
    }
  };

  if (loading) return null;

  const ready = status?.connected && status?.onboarded && status?.charges_enabled;
  const pending = status?.connected && !ready;

  return (
    <section data-testid="payouts-panel" className={`mb-10 border ${ready ? "border-[#CCFF00]/40 bg-[#CCFF00]/5" : pending ? "border-[#FFC93B]/40 bg-[#FFC93B]/5" : "border-white/15 bg-[#0A0A0A]"} p-6`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="p-2 border border-white/15">
            <Landmark className="w-5 h-5" />
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/60">
              // Payouts · Stripe Connect
            </div>
            <div className="font-display font-bold text-xl mt-1 flex items-center gap-2">
              {ready ? (
                <>
                  <CheckCircle2 className="w-5 h-5 text-[#CCFF00]" />
                  Direct payouts enabled
                </>
              ) : pending ? (
                <>
                  <AlertCircle className="w-5 h-5 text-[#FFC93B]" />
                  Onboarding incomplete
                </>
              ) : (
                <>Get paid directly to your bank</>
              )}
            </div>
            <p className="mt-2 text-white/70 text-sm max-w-2xl leading-relaxed">
              {ready
                ? `Every sale routes ${status.creator_share_pct}% to your Stripe account and keeps ${status.platform_fee_pct}% as the platform fee. Payouts to your bank happen on Stripe's schedule.`
                : pending
                ? "You started Stripe onboarding but haven't finished. Complete the remaining steps to start receiving direct payouts."
                : `Connect Stripe to receive ${status?.creator_share_pct ?? 90}% of every sale straight to your bank. Without this, sales go to the platform balance (still tracked as your net earnings in the dashboard).`}
            </p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <button
            data-testid="payouts-onboard-btn"
            onClick={onboard}
            disabled={busy}
            className={`inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] px-4 py-2.5 ${ready ? "border border-[#CCFF00]/40 text-[#CCFF00] hover:bg-[#CCFF00]/10" : "bg-[#FF3366] text-white hover:bg-[#FF3366]/90"} disabled:opacity-40`}
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ExternalLink className="w-3.5 h-3.5" />}
            {ready ? "Manage account" : pending ? "Finish onboarding" : "Connect Stripe"}
          </button>
          {status?.account_id && (
            <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/40">
              {status.account_id}
            </div>
          )}
        </div>
      </div>
      {ready && (
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2 text-[10px] font-mono uppercase tracking-[0.2em]">
          <Chip ok label="Charges" />
          <Chip ok label="Transfers" />
          <Chip ok={status.payouts_enabled} label={status.payouts_enabled ? "Payouts" : "Payouts pending"} />
        </div>
      )}
      {status?.connect_disabled && (
        <div data-testid="payouts-disabled-notice" className="mt-4 border border-[#FFC93B]/40 bg-[#FFC93B]/5 p-4 text-sm text-white/80 leading-relaxed">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFC93B] mb-2">
            // Platform action required
          </div>
          {status.disabled_reason}
          <div className="mt-3">
            <a
              href="https://dashboard.stripe.com/connect"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] border border-white/20 hover:border-white/50 px-3 py-2"
            >
              <ExternalLink className="w-3 h-3" /> Open Stripe Connect settings
            </a>
          </div>
        </div>
      )}
    </section>
  );
}

function Chip({ label, ok }) {
  return (
    <div className={`border px-3 py-1.5 ${ok ? "border-[#CCFF00]/40 text-[#CCFF00]" : "border-white/20 text-white/60"}`}>
      · {label}
    </div>
  );
}
